const getMinMax = (values: number[]) => {
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;

    values.forEach(value => {
        if (value < min) min = value;
        if (value > max) max = value;
    });

    return { min, max };
};

/** Where the Y axis should sit relative to the data. */
export type YAxisFloor =
    /** Start at 0, so bar length reads as magnitude. Only drops below 0 for negative data. */
    | 'zero'
    /** Sit just under the lowest value, but never below 0. Keeps small variation readable. */
    | 'nearData'
    /** Sit just under the lowest value, negatives included. */
    | 'auto';

export const buildYAxisDomain = (
    values: number[],
    floor: YAxisFloor = 'auto',
): [number, number] | undefined => {
    if (values.length === 0) return undefined;

    const { min, max } = getMinMax(values);

    const lowerBound = (padding: number) => {
        // Only drop below 0 when the data genuinely goes negative.
        if (floor === 'zero') return min < 0 ? min - padding : 0;
        if (floor === 'nearData') return Math.max(0, min - padding);
        return min - padding;
    };

    if (min === max) {
        if (min === 0) return [0, 1];

        const padding = Math.max(1, Math.ceil(Math.abs(min) * 0.1));
        return [lowerBound(padding), max + padding];
    }

    const padding = Math.max(1, Math.ceil((max - min) * 0.05));
    return [lowerBound(padding), max + padding];
};

export const buildPercentYAxisDomain = (values: number[]): [number, number] | undefined => {
    if (values.length === 0) return undefined;

    const { min, max } = getMinMax(values);

    if (min === max) {
        if (max === 0) return [0, 100];

        const lowerPadding = Math.max(1, Math.ceil(Math.abs(min) * 0.1));
        return [Math.max(0, min - lowerPadding), max];
    }

    const lowerPadding = Math.max(1, Math.ceil((max - min) * 0.05));
    return [Math.max(0, min - lowerPadding), max];
};
